import type { CSSProperties, ElementType, ReactNode } from 'react';
import styles from './Text.module.css';

/**
 * Mirrors the Figma text styles, which are named `Text {size}/leading-{leading}/{weight}`.
 *
 * `size` + `leading` together pick the font-size/line-height pair; `weight` picks
 * the weight. `Display xs` is not offered here — `KPI` is its only caller and
 * styles it locally; see KPI.module.css.
 *
 * The 2026-08 design update narrowed which combinations Figma actually cuts: only
 * `sm` still has both leadings, and `xxs`, `xs` and `lg` had a single weight
 * each. The 2026-09 update gave `xs` its Regular cut back — `Text xs/Regular`
 * and `Text xs/Medium` both exist now — and Regular is the one the KPI labels
 * and the sidebar's section headings bind. `xxs` and `lg` are still Medium only.
 *
 * This prop surface is deliberately kept wide anyway, so an unavailable
 * combination resolves to the one real style rather than failing to compile.
 * The maps below record which combinations are real.
 */
export type TextSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
/** Only `sm` is cut both ways in Figma (12/12 and 12/16); every other size ignores this. */
export type TextLeading = 'none' | 'normal';
/** Figma cuts all three for `sm` and `md`, Regular and Medium for `xs`; `xxs` and `lg` are Medium only. */
export type TextWeight = 'regular' | 'medium' | 'semibold';

/** Semantic colour tokens valid for text. */
export type TextColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'disabled'
  | 'placeholder'
  | 'link'
  | 'linkHover'
  | 'success'
  | 'error'
  | 'warning'
  | 'inverse';

export type TextProps = {
  as?: ElementType;
  size?: TextSize;
  /** Only `xs` and `sm` have both leadings in Figma; larger sizes ignore this. */
  leading?: TextLeading;
  weight?: TextWeight;
  color?: TextColor;
  /** Truncate to a single line with an ellipsis. */
  truncate?: boolean;
  className?: string;
  style?: CSSProperties;
  /** So a dialog or field can point `aria-labelledby` / `aria-describedby` at it. */
  id?: string;
  children?: ReactNode;
};

const sizeClass: Record<string, string> = {
  /* Figma has a single `Text xxs/Medium` — 8/8 — so both leadings resolve to it. */
  'xxs-none': styles.xxs,
  'xxs-normal': styles.xxs,
  /*
   * `Text xs` used to be cut both leadings, 12/12 and 12/18. The 2026-08 update
   * collapsed it to a single 10/10 style, so leading no longer selects anything
   * here — that part still holds. What came back in 2026-09 is the *weight*
   * axis: `xs` is cut Regular and Medium again, which `weightClass` already
   * handles independently of size.
   */
  'xs-none': styles.xs,
  'xs-normal': styles.xs,
  /* sm is the only size Figma still cuts both ways: 12/12 and 12/18. */
  'sm-none': styles.smNone,
  'sm-normal': styles.smNormal,
  /* md and lg have a single leading each in Figma; leading has no effect. */
  'md-none': styles.md,
  'md-normal': styles.md,
  'lg-none': styles.lg,
  'lg-normal': styles.lg,
};

const weightClass: Record<TextWeight, string> = {
  regular: styles.regular,
  medium: styles.medium,
  semibold: styles.semibold,
};

const colorClass: Record<TextColor, string> = {
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
  tertiary: styles.colorTertiary,
  disabled: styles.colorDisabled,
  placeholder: styles.colorPlaceholder,
  link: styles.colorLink,
  linkHover: styles.colorLinkHover,
  success: styles.colorSuccess,
  error: styles.colorError,
  warning: styles.colorWarning,
  inverse: styles.colorInverse,
};

export function Text({
  as: Component = 'span',
  size = 'sm',
  leading = 'none',
  weight = 'regular',
  color = 'primary',
  truncate = false,
  className,
  style,
  id,
  children,
}: TextProps) {
  const classes = [
    styles.text,
    sizeClass[`${size}-${leading}`],
    weightClass[weight],
    colorClass[color],
    truncate ? styles.truncate : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classes} style={style} id={id}>
      {children}
    </Component>
  );
}
